import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useReducedMotion } from 'framer-motion';
import { useAuth } from '../../../hooks/useAuth';
import { useSubscription } from '../../../hooks/useSubscription';
import { useMessages } from '../../../hooks/useMessages';
import { useCompany } from '../../../hooks/useCompany';
import { useFavorites } from '../../../hooks/useFavorites';
import { useChat, CHAT_COMING_SOON } from '../../../hooks/useChat';
import { useEntitlements } from '../../../hooks/useEntitlements';
import { useFocusTrap } from '../../../hooks/useFocusTrap';
import { useDismiss } from '../../../hooks/useDismiss';
import { isProfessional } from '../../../utils/isProfessional';
import { useSmoothScroll } from '../../providers/SmoothScrollProvider';
import SegmentedControl from '../../base/SegmentedControl';
import CompanyBadge from '../../common/CompanyBadge';
import SafaqatLogo from '../../brand/SafaqatLogo';
import ThemeToggle from '../ThemeToggle';
import { useListPropertyAction } from './useListPropertyAction';
import {
  ADDONS_ITEM,
  ADS_BOARD_ENABLED,
  BUNDLES_ITEM,
  BUY_ITEMS,
  COINS_ITEM,
  CREATE_ACCOUNT_ITEM,
  FIND_PRO_ITEMS,
  HELP_ITEMS,
  INVEST_ITEMS,
  MARKET_TOOLS_ITEM,
  MY_ADDONS_ITEM,
  NON_SAUDI_ITEM,
  POST_REQUEST_ITEM,
  PRO_ROLES_ENABLED,
  PRO_SERVICES_ITEM,
  REGISTER_PRO_ITEMS,
  RENT_ITEMS,
  REQUESTS_BOARD_ITEM,
  SERVICE_WORKFLOWS_ENABLED,
  SUBSCRIPTIONS_ITEM,
  accountBadge,
  canSeeSubscriptions,
  isAdvertiserAccount,
  signInState,
  userDisplayName,
  type NavItem,
} from './navItems';

/**
 * The site menu drawer (owner, 2026-10; Wasalt-style). Opened by the one
 * hamburger beside the logo at every width.
 *
 * Top to bottom: a guest card («سجّل الدخول لتجربة مخصّصة») or the signed-in
 * card; ONE full-width «أضف عقارك» call to action; accordion cards (icon tile,
 * title, subtitle, chevron; one open at a time) for العقارات, خدماتنا, ابحث عن,
 * سجل معنا كمهني (guests), حسابي (signed in) and المساعدة والدعم, with «أضف طلبك»
 * as a plain link card; then the language and appearance rows and sign-out.
 * Every destination appears once.
 *
 * It is a modal dialog: role="dialog" + aria-modal, focus moves in and is
 * trapped while open, Escape or a press outside closes it, and focus returns
 * to the hamburger. The page behind is scroll-locked (Lenis included). Closed,
 * it stays mounted for the slide but is visibility:hidden, so its links are
 * neither tabbable nor read out.
 */

interface SidebarProps {
  /** DOM id; the hamburger's aria-controls points at it. */
  id?: string;
  open: boolean;
  onClose: () => void;
  /** The hamburger. Focus goes back to it when the drawer closes. */
  returnFocusRef: RefObject<HTMLElement | null>;
}

type SectionKey = 'properties' | 'services' | 'findPro' | 'registerPro' | 'account' | 'help';

/** A row inside an open card. */
interface RowLink {
  to: string;
  icon: string;
  label: string;
  /** A count shown at the inline end (favourite lists, unread enquiries). */
  count?: number;
  /** Red count for unread items; teal otherwise. */
  alert?: boolean;
  state?: unknown;
}

const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

const CARD =
  'rounded-2xl border border-[var(--pv-border-soft)] bg-[var(--pv-color-surface-strong)]';

/** A 2.5rem tile around a 1.25rem icon: the 2:1 proportion rule. */
const ICON_TILE =
  'grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[var(--pv-color-surface-sunken)] text-[var(--pv-color-primary)]';

const FOCUS_INSET =
  'focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--pv-color-primary)]';

const CARD_HEADER = [
  'flex min-h-[3.75rem] w-full items-center gap-3 rounded-2xl p-3 text-start transition-colors duration-150',
  'hover:bg-[var(--pv-color-surface-soft)]',
  FOCUS_INSET,
].join(' ');

/** A row's layout and type: label 0.9375rem with a 1.125rem icon (proportion rule). */
const ROW_BASE =
  'flex min-h-[2.75rem] w-full items-center gap-3 rounded-xl px-3 text-start text-[0.9375rem] leading-snug text-[var(--pv-color-text)]';

const ROW = [
  ROW_BASE,
  'transition-colors duration-150 hover:bg-[var(--pv-color-surface-sunken)] hover:text-[var(--pv-color-primary)]',
  FOCUS_INSET,
].join(' ');

const ROW_ICON = 'shrink-0 text-[1.125rem] leading-none text-[var(--pv-color-primary)]';

const GROUP_LABEL = 'px-3 pb-1 pt-2 text-[0.875rem] font-semibold text-[var(--pv-color-text-muted)]';

function CardText({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <span className="flex min-w-0 flex-1 flex-col">
      <span className="text-[1rem] font-semibold leading-snug text-[var(--pv-color-text)]">{title}</span>
      {subtitle && (
        // Wraps rather than truncating: on a 360px phone the card's text column is ~11rem.
        <span className="line-clamp-2 text-[0.875rem] leading-snug text-[var(--pv-color-text-muted)]">{subtitle}</span>
      )}
    </span>
  );
}

function CountBadge({ value, alert }: { value: number; alert?: boolean }) {
  return (
    <span
      className={[
        'ms-auto inline-flex min-w-[1.5rem] items-center justify-center rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums',
        alert
          ? 'bg-[#DF2F2B] text-white'
          : 'bg-[var(--pv-color-surface-sunken)] text-[var(--pv-color-primary)]',
      ].join(' ')}
    >
      {value > 9 && alert ? '9+' : value}
    </span>
  );
}

export default function Sidebar({ id = 'sidebar', open, onClose, returnFocusRef }: SidebarProps) {
  const { t, i18n } = useTranslation(['nav', 'common']);
  const { user, signOut } = useAuth();
  const { isSubscribed } = useSubscription();
  const { unreadCount } = useMessages();
  const { company } = useCompany();
  const { lists } = useFavorites();
  const { openChat } = useChat();
  const { stop: stopScroll, start: startScroll } = useSmoothScroll();
  const navigate = useNavigate();
  const location = useLocation();
  const listProperty = useListPropertyAction();
  const reduceMotion = useReducedMotion() ?? false;

  const panelRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const sectionIdBase = useId();
  // Single-open accordion; null = all collapsed.
  const [openSection, setOpenSection] = useState<SectionKey | null>(null);

  const isRTL = i18n.dir() === 'rtl';
  const language = (i18n.resolvedLanguage || i18n.language || 'ar').startsWith('en') ? 'en' : 'ar';

  // «لوحة الطلبات» is for professional accounts only (owner decision). The
  // session answers most cases at once; the server knows the rest (live
  // professional activities), so ask it only while the drawer is open for a
  // signed-in user the session cannot place.
  const sessionPro = isProfessional(user);
  const entitlements = useEntitlements({ enabled: open && Boolean(user) && !sessionPro });
  const showRequestsBoard = ADS_BOARD_ENABLED && Boolean(user) && (sessionPro || entitlements.isProfessional);

  useFocusTrap(panelRef, open, { initialFocus: closeButtonRef, returnFocus: returnFocusRef });

  // Escape and presses outside the panel (the scrim, or a floating widget above it).
  useDismiss({ enabled: open, refs: [panelRef], onDismiss: () => onClose() });

  // Lock the page behind while open. Lenis (home page) is stopped too, and wheel
  // events inside the panel are kept from reaching it.
  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    stopScroll();
    const panel = panelRef.current;
    const keepWheelInside = (event: WheelEvent) => event.stopPropagation();
    panel?.addEventListener('wheel', keepWheelInside, { passive: true });
    return () => {
      panel?.removeEventListener('wheel', keepWheelInside);
      document.body.style.overflow = previousOverflow;
      startScroll();
    };
  }, [open, stopScroll, startScroll]);

  const toggleSection = (key: SectionKey) => setOpenSection((prev) => (prev === key ? null : key));

  const handleSignOut = async () => {
    try {
      await signOut();
      onClose();
      navigate('/');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const toRow = (item: NavItem): RowLink => ({ to: item.to, icon: item.icon, label: t(item.labelKey) });

  const renderRow = (row: RowLink) => (
    <li key={row.to}>
      <Link to={row.to} state={row.state} onClick={onClose} className={ROW}>
        <i className={`${row.icon} ${ROW_ICON}`} aria-hidden="true" />
        <span className="min-w-0 flex-1">{row.label}</span>
        {row.count !== undefined && <CountBadge value={row.count} alert={row.alert} />}
      </Link>
    </li>
  );

  const renderGroup = (key: string, label: string, rows: RowLink[]) => {
    const labelId = `${sectionIdBase}-${key}`;
    return (
      <div key={key} role="group" aria-labelledby={labelId}>
        <p id={labelId} className={GROUP_LABEL}>
          {label}
        </p>
        <ul>{rows.map(renderRow)}</ul>
      </div>
    );
  };

  const renderSection = (key: SectionKey, icon: string, title: string, subtitle: string, body: ReactNode) => {
    const isOpen = openSection === key;
    const headerId = `${sectionIdBase}-${key}-header`;
    const panelId = `${sectionIdBase}-${key}-panel`;
    return (
      <div key={key} className={CARD}>
        <h3>
          <button
            id={headerId}
            type="button"
            aria-expanded={isOpen}
            aria-controls={panelId}
            onClick={() => toggleSection(key)}
            className={CARD_HEADER}
          >
            <span className={ICON_TILE} aria-hidden="true">
              <i className={`${icon} text-[1.25rem] leading-none`} />
            </span>
            <CardText title={title} subtitle={subtitle} />
            <i
              className={[
                'ri-arrow-down-s-line shrink-0 text-[1.25rem] leading-none text-[var(--pv-color-text-muted)]',
                reduceMotion ? '' : 'transition-transform duration-200',
                isOpen ? 'rotate-180' : '',
              ].join(' ')}
              aria-hidden="true"
            />
          </button>
        </h3>
        <div
          id={panelId}
          role="region"
          aria-labelledby={headerId}
          hidden={!isOpen}
          className="border-t border-[var(--pv-border-subtle)] p-1.5"
        >
          {body}
        </div>
      </div>
    );
  };

  // ── Section contents ────────────────────────────────────────────────────
  const propertiesBody = (
    <>
      {renderGroup('rent', t('top.rent'), RENT_ITEMS.map(toRow))}
      {renderGroup('buy', t('top.buy'), BUY_ITEMS.map(toRow))}
      {renderGroup('invest', t('top.invest'), INVEST_ITEMS.map(toRow))}
      <ul className="mt-1 border-t border-[var(--pv-border-subtle)] pt-1">{renderRow(toRow(NON_SAUDI_ITEM))}</ul>
    </>
  );

  const serviceRows: RowLink[] = [
    toRow(MARKET_TOOLS_ITEM),
    ...(canSeeSubscriptions(user) ? [toRow(SUBSCRIPTIONS_ITEM)] : []),
    ...(SERVICE_WORKFLOWS_ENABLED ? [toRow(PRO_SERVICES_ITEM)] : []),
    ...(showRequestsBoard ? [toRow(REQUESTS_BOARD_ITEM)] : []),
    toRow(ADDONS_ITEM),
    toRow(BUNDLES_ITEM),
    ...(user ? [toRow(COINS_ITEM), toRow(MY_ADDONS_ITEM)] : []),
  ];

  const advertiser = isAdvertiserAccount(user);
  const accountRows: RowLink[] = user
    ? [
        { to: '/profile', icon: 'ri-user-line', label: t('common:navigation.profile') },
        ...(PRO_ROLES_ENABLED
          ? [{ to: '/account/professional', icon: 'ri-briefcase-line', label: t('account.grow') }]
          : []),
        // Only independent brokers get /dashboard; company members have the
        // richer /company/dashboard below.
        ...(isSubscribed && !company
          ? [{ to: '/dashboard', icon: 'ri-dashboard-line', label: t('common:navigation.sidebar.dashboard') }]
          : []),
        ...(isSubscribed || company
          ? [{ to: '/listings', icon: 'ri-building-line', label: t('common:navigation.sidebar.myListings') }]
          : []),
        ...(advertiser
          ? [{ to: '/broker/requests', icon: 'ri-file-list-2-line', label: t('common:navigation.sidebar.brokerRequests') }]
          : []),
        ...(company
          ? [
              { to: '/company/dashboard', icon: 'ri-building-4-line', label: t('common:navigation.companyDashboard') },
              { to: '/company/team', icon: 'ri-team-line', label: t('common:navigation.teamManagement') },
            ]
          : []),
        { to: '/my-requests', icon: 'ri-folder-user-line', label: t('common:navigation.sidebar.myRequests') },
        {
          to: '/favorites',
          icon: 'ri-heart-line',
          label: t('common:navigation.sidebar.favorites'),
          count: lists.length > 1 ? lists.length : undefined,
        },
        ...(isSubscribed || company
          ? [
              {
                to: '/enquiries',
                icon: 'ri-notification-3-line',
                label: t('common:navigation.enquiries'),
                count: unreadCount > 0 ? unreadCount : undefined,
                alert: true,
              },
            ]
          : []),
        { to: '/complaints/my', icon: 'ri-file-list-3-line', label: t('common:navigation.myComplaints') },
        { to: '/settings', icon: 'ri-settings-4-line', label: t('common:navigation.sidebar.accountSettings') },
      ]
    : [];

  const helpBody = (
    <ul>
      {HELP_ITEMS.map(toRow).map(renderRow)}
      <li>
        {CHAT_COMING_SOON ? (
          // Not launched yet: say so rather than offer a dead button.
          <span className={ROW_BASE}>
            <i className={`ri-customer-service-2-line ${ROW_ICON}`} aria-hidden="true" />
            <span className="min-w-0 flex-1">{t('sidebar.help.liveChat')}</span>
            <span className="ms-auto rounded-full bg-[var(--pv-color-surface-sunken)] px-2 py-0.5 text-xs text-[var(--pv-color-text-muted)]">
              {t('sidebar.comingSoon')}
            </span>
          </span>
        ) : (
          <button
            type="button"
            onClick={() => {
              onClose();
              openChat();
            }}
            className={ROW}
          >
            <i className={`ri-customer-service-2-line ${ROW_ICON}`} aria-hidden="true" />
            <span className="min-w-0 flex-1">{t('sidebar.help.liveChat')}</span>
          </button>
        )}
      </li>
    </ul>
  );

  const badge = accountBadge(user, { includeIndividual: true });
  const companyBadge = user?.companyBadge as
    | { companyId: string; companyName: string; companyNameAr: string; teamRole: 'OWNER' | 'ADMIN' | 'AGENT' | 'VIEWER' }
    | null
    | undefined;

  // ── Motion: slide in from the inline-start edge; hidden only after sliding out ──
  const transition = reduceMotion
    ? undefined
    : open
      ? `transform 300ms ${EASE}`
      : `transform 300ms ${EASE}, visibility 0s linear 300ms`;
  const panelStyle: CSSProperties = { visibility: open ? 'visible' : 'hidden', transition };
  const scrimStyle: CSSProperties = {
    visibility: open ? 'visible' : 'hidden',
    transition: reduceMotion
      ? undefined
      : open
        ? 'opacity 300ms ease-out'
        : 'opacity 300ms ease-out, visibility 0s linear 300ms',
  };

  return (
    <>
      {/* Scrim. A solid tint of the deepest brand teal: no blur (THEME §5). */}
      <div
        aria-hidden="true"
        data-sidebar-scrim=""
        onClick={onClose}
        style={scrimStyle}
        className={[
          'fixed inset-0 z-[60] bg-[rgba(0,23,19,0.48)]',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        ].join(' ')}
      />

      <aside
        ref={panelRef}
        id={id}
        dir={i18n.dir()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-lenis-prevent=""
        style={panelStyle}
        className={[
          'fixed inset-y-0 z-[70] flex w-[22rem] max-w-[85vw] flex-col',
          'bg-[var(--pv-color-surface)] shadow-brand-md',
          isRTL ? 'right-0' : 'left-0',
          open ? 'translate-x-0' : isRTL ? 'translate-x-full' : '-translate-x-full',
        ].join(' ')}
      >
        {/* Header: the logo and the close button. The dialog's name is the sr-only title. */}
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-[var(--pv-border-soft)] px-4 py-2">
          <h2 id={titleId} className="sr-only">
            {t('menu.title')}
          </h2>
          <SafaqatLogo className="h-12 w-auto" title="" />
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label={t('menu.close')}
            className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl text-[var(--pv-color-text-muted)] transition-colors hover:bg-[var(--pv-color-surface-sunken)] hover:text-[var(--pv-color-text)] ${FOCUS_INSET}`}
          >
            <i className="ri-close-line text-[1.375rem] leading-none" aria-hidden="true" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-4">
          {/* Who you are, or an invitation to sign in */}
          {user ? (
            <div className={`${CARD} flex items-center gap-3 p-4`}>
              <span
                className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[var(--pv-color-surface-sunken)] text-[var(--pv-color-primary)] ring-1 ring-[var(--pv-border-soft)]"
                aria-hidden="true"
              >
                <i className="ri-user-line text-[1.375rem] leading-none" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[1rem] font-semibold text-[var(--pv-color-text)]">
                  {userDisplayName(user) || t('common:auth.signIn')}
                </p>
                {user.email && (
                  <p className="truncate text-[0.875rem] text-[var(--pv-color-text-muted)]">
                    <bdi>{user.email}</bdi>
                  </p>
                )}
                {(badge || companyBadge) && (
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    {badge && (
                      <span
                        className={[
                          'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold',
                          badge.tone === 'neutral'
                            ? 'bg-[var(--pv-color-surface-sunken)] text-[var(--pv-color-text-muted)]'
                            : 'bg-[#DDEEEB] text-[#004D40] dark:bg-[#0A4A3E] dark:text-[#7FD6C3]',
                        ].join(' ')}
                      >
                        {t(badge.labelKey)}
                      </span>
                    )}
                    {companyBadge && (
                      <CompanyBadge
                        teamRole={companyBadge.teamRole}
                        companyName={isRTL ? companyBadge.companyNameAr : companyBadge.companyName}
                        size="sm"
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className={`${CARD} p-4`}>
              <div className="flex items-start gap-3">
                <span className={ICON_TILE} aria-hidden="true">
                  <i className="ri-user-smile-line text-[1.25rem] leading-none" />
                </span>
                <div className="min-w-0">
                  <p className="text-[1rem] font-semibold leading-snug text-[var(--pv-color-text)]">
                    {t('sidebar.guestTitle')}
                  </p>
                  <p className="mt-1 text-[0.875rem] leading-relaxed text-[var(--pv-color-text-muted)]">
                    {t('sidebar.guestBody')}
                  </p>
                </div>
              </div>
              <Link
                to="/signin"
                state={signInState(location)}
                onClick={onClose}
                className="mt-4 flex min-h-[2.75rem] w-full items-center justify-center gap-2 rounded-xl border border-[#004D40] px-4 text-[0.9375rem] font-semibold text-[#004D40] transition-colors hover:bg-[var(--pv-color-surface-sunken)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#004D40]/20 dark:border-[#3FBBA2] dark:text-[#3FBBA2]"
              >
                <i className="ri-login-circle-line text-[1.125rem] leading-none" aria-hidden="true" />
                {t('signIn')}
              </Link>
              <p className="mt-3 text-center text-[0.875rem] text-[var(--pv-color-text-muted)]">
                {t('sidebar.noAccount')}{' '}
                <Link
                  to={CREATE_ACCOUNT_ITEM.to}
                  onClick={onClose}
                  className="font-semibold text-[var(--pv-color-primary)] underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--pv-color-primary)]"
                >
                  {t('register.createAccount')}
                </Link>
              </p>
            </div>
          )}

          {/* The one solid call to action in the drawer */}
          <Link
            to={listProperty.to}
            state={listProperty.state}
            onClick={(event) => listProperty.onClick(event, onClose)}
            className="mt-4 flex min-h-[3rem] w-full items-center justify-center gap-2 rounded-xl bg-[#004D40] px-4 text-[1rem] font-semibold text-white shadow-brand-xs transition-colors hover:bg-[#003930] active:bg-[#002A23] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#004D40] focus-visible:ring-offset-2 dark:bg-[#3FBBA2] dark:text-[#002A23] dark:hover:bg-[#7FD6C3] dark:focus-visible:ring-offset-[#041C18]"
          >
            <i className="ri-add-circle-line text-[1.25rem] leading-none" aria-hidden="true" />
            {t('common:navigation.listProperty')}
          </Link>

          <nav aria-labelledby={titleId} className="mt-4 space-y-2">
            {renderSection('properties', 'ri-building-line', t('sidebar.properties.title'), t('sidebar.properties.subtitle'), propertiesBody)}

            {/* «أضف طلبك»: a destination, not a group, so a plain link card */}
            <div className={CARD}>
              <Link to={POST_REQUEST_ITEM.to} onClick={onClose} className={CARD_HEADER}>
                <span className={ICON_TILE} aria-hidden="true">
                  <i className={`${POST_REQUEST_ITEM.icon} text-[1.25rem] leading-none`} />
                </span>
                <CardText title={t(POST_REQUEST_ITEM.labelKey)} subtitle={t('sidebar.postRequest.subtitle')} />
                <i
                  className={`${isRTL ? 'ri-arrow-left-s-line' : 'ri-arrow-right-s-line'} shrink-0 text-[1.25rem] leading-none text-[var(--pv-color-text-muted)]`}
                  aria-hidden="true"
                />
              </Link>
            </div>

            {renderSection('services', 'ri-apps-2-line', t('sidebar.services.title'), t('sidebar.services.subtitle'), <ul>{serviceRows.map(renderRow)}</ul>)}

            {renderSection('findPro', 'ri-user-search-line', t('sidebar.findPro.title'), t('sidebar.findPro.subtitle'), <ul>{FIND_PRO_ITEMS.map(toRow).map(renderRow)}</ul>)}

            {!user && PRO_ROLES_ENABLED &&
              renderSection(
                'registerPro',
                'ri-user-add-line',
                t('sidebar.registerPro.title'),
                t('sidebar.registerPro.subtitle'),
                <ul>{REGISTER_PRO_ITEMS.map(toRow).map(renderRow)}</ul>,
              )}

            {user &&
              renderSection('account', 'ri-user-3-line', t('sidebar.account.title'), t('sidebar.account.subtitle'), <ul>{accountRows.map(renderRow)}</ul>)}

            {renderSection('help', 'ri-question-line', t('sidebar.help.title'), t('sidebar.help.subtitle'), helpBody)}
          </nav>

          {/* Language and appearance */}
          <div className={`${CARD} mt-4 divide-y divide-[var(--pv-border-subtle)]`}>
            <div className="flex min-h-[3.5rem] flex-wrap items-center justify-between gap-3 px-4 py-2">
              <span className="flex items-center gap-3 text-[0.9375rem] text-[var(--pv-color-text)]">
                <i className={`ri-global-line ${ROW_ICON}`} aria-hidden="true" />
                {t('sidebar.language')}
              </span>
              <SegmentedControl
                value={language}
                onChange={(next) => void i18n.changeLanguage(next)}
                options={[
                  { value: 'ar', label: t('language.ar') },
                  { value: 'en', label: t('language.en') },
                ]}
                ariaLabel={t('sidebar.language')}
                size="sm"
              />
            </div>
            <div className="flex min-h-[3.5rem] items-center justify-between gap-3 px-4 py-2">
              <span className="flex items-center gap-3 text-[0.9375rem] text-[var(--pv-color-text)]">
                <i className={`ri-contrast-2-line ${ROW_ICON}`} aria-hidden="true" />
                {t('sidebar.theme')}
              </span>
              <ThemeToggle />
            </div>
          </div>

          {user && (
            <button
              type="button"
              onClick={handleSignOut}
              className="mt-4 flex min-h-[2.75rem] w-full items-center justify-center gap-2 rounded-xl border border-[var(--pv-border-soft)] px-4 text-[0.9375rem] font-semibold text-[#C0322A] transition-colors hover:bg-[#FDEAEA] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C0322A]/25 dark:text-[#FF8A80] dark:hover:bg-[#2E1613]"
            >
              <i className="ri-logout-circle-line text-[1.125rem] leading-none" aria-hidden="true" />
              {t('sidebar.signOut')}
            </button>
          )}
        </div>
      </aside>
    </>
  );
}
